import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Search } from "lucide-react";
import { AdminTable } from "components/shared/AdminTable";
import { AdminTablePagination } from "components/shared/AdminTablePagination";
import { useSiteKeys } from "./siteKeysApi";
import { formatKeyDate, formatValidity } from "./siteKeyLabels";
import { CredentialValue, ExternalSyncCell, GuestLink } from "./SiteKeyParts";
import {
  KEYS_PAGE_SIZE_OPTIONS,
  useDebouncedFilter,
  useKeysSearchParams,
} from "./useKeysSearchParams";

/**
 * The Keys tab's Fobs list. The fob number is the primary column; the credential is secondary.
 * The External sync column shows only on Schlage sites that share physical keys with sister sites.
 */
export default function SiteFobsList({
  siteId,
  showExternalSync,
}: {
  siteId: string;
  showExternalSync: boolean;
}) {
  const location = useLocation();
  const { page, pageSize, getParam, setPage, setPageSize, setFilters } =
    useKeysSearchParams();
  const search = getParam("q");
  const onCommitSearch = React.useCallback(
    (value: string) => setFilters({ q: value }),
    [setFilters],
  );
  const [searchInput, setSearchInput] = useDebouncedFilter(search, onCommitSearch);

  const { data, isLoading, isError, isFetching, isPlaceholderData } = useSiteKeys(
    siteId,
    search,
    page * pageSize,
    pageSize,
  );
  const keys = data ?? [];
  const columnCount = showExternalSync ? 6 : 5;
  // Return here (with search and page) from the fob detail's breadcrumb.
  const linkState = { from: `${location.pathname}${location.search}` };

  return (
    <AdminTable
      aria-label="Site fobs"
      aria-busy={isFetching}
      toolbar={
        <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center">
          <label className="relative block w-full sm:w-80">
            <span className="sr-only">Search fobs</span>
            <Search
              aria-hidden="true"
              className="absolute -translate-y-1/2 pointer-events-none left-3 top-1/2 size-4 text-slate-400"
            />
            <input
              className="w-full h-10 py-2 pr-3 text-sm transition-colors bg-white border rounded-md outline-none border-slate-200 pl-9 text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              placeholder="Search by fob # or credential"
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>
        </div>
      }
      footer={
        <AdminTablePagination
          currentPage={page}
          pageSize={pageSize}
          pageSizeOptions={KEYS_PAGE_SIZE_OPTIONS}
          visibleRowCount={keys.length}
          // Also while the next page loads (the previous page stays on screen meanwhile).
          isLoading={isLoading || isPlaceholderData}
          onNext={() => setPage(page + 1)}
          onPrevious={() => setPage(Math.max(0, page - 1))}
          onPageSizeChange={setPageSize}
          disablePrevious={page === 0}
          // The endpoint returns no total, so offer the next page whenever this one is full.
          disableNext={keys.length < pageSize}
        />
      }
    >
      <thead>
        <tr>
          <th scope="col">Fob #</th>
          <th scope="col">Assigned to</th>
          <th scope="col">Valid</th>
          <th scope="col">Last encoded</th>
          <th scope="col">Credential</th>
          {showExternalSync && <th scope="col">External sync</th>}
        </tr>
      </thead>
      <tbody>
        {isError ? (
          <tr>
            <td colSpan={columnCount} className="py-16 text-sm text-center text-rose-700">
              Error loading fobs.
            </td>
          </tr>
        ) : isLoading ? (
          <tr>
            <td colSpan={columnCount} className="py-16 text-sm text-center text-slate-500">
              Loading fobs...
            </td>
          </tr>
        ) : keys.length === 0 ? (
          <tr>
            <td colSpan={columnCount} className="py-16 text-sm text-center text-slate-500">
              {search ? "No fobs match your search." : "No fobs found."}
            </td>
          </tr>
        ) : (
          keys.map((key) => (
            <tr key={key.credential}>
              <td className="whitespace-nowrap">
                <Link
                  to={`/admin/sites/${siteId}/keys/${encodeURIComponent(key.credential)}`}
                  state={linkState}
                >
                  {key.badgeId || "(no fob #)"}
                </Link>
              </td>
              <td>
                {key.guestId ? (
                  <GuestLink guestId={key.guestId} />
                ) : key.physicalKeyGroupId ? (
                  <span className="text-slate-500">Key group</span>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </td>
              <td className="whitespace-nowrap">
                {formatValidity(key.validFrom, key.validTo)}
              </td>
              <td className="whitespace-nowrap">{formatKeyDate(key.encodedTimestamp)}</td>
              <td>
                <CredentialValue credential={key.credential} truncate />
              </td>
              {showExternalSync && (
                <td>
                  <ExternalSyncCell syncs={key.externalSyncs} />
                </td>
              )}
            </tr>
          ))
        )}
      </tbody>
    </AdminTable>
  );
}
